import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, later, next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import tinymce from 'tinymce';

import type { PopupData, PopupListItem, RequestError, Service } from 'webapp-types';
import {
	NewReportSlideFragmentLayout,
	NewReportSlideLayout,
	ReportFragmentAction,
} from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';
import {
	copyObject,
	getOrderFromPosition,
	getPositionFromOrder,
	sortBy,
} from 'webapp/utils/common';
import { stripAllHtml } from 'webapp/utils/html';

import definite from 'webapp/helpers/definite';
import { getLeadingEmoji, hasLeadingEmoji } from 'webapp/helpers/emoji';
import eq from 'webapp/helpers/eq';
import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ReportsCoverPagelessComponent, {
	CoverAction,
} from 'webapp/components/reports/cover-pageless';
import type {
	Args,
	ReportFragmentActionAddData,
	ReportFragmentActionData,
} from 'webapp/components/reports/editor-base';
import ReportsEditorBaseComponent, {
	OrderProperty,
	type OrdersData,
} from 'webapp/components/reports/editor-base';
import ReportsEditorBlockSettingsComponent from 'webapp/components/reports/editor/block-settings';
import ReportsEditorFragmentBlockComponent from 'webapp/components/reports/editor/fragment/block';
import ReportsEditorFragmentBoardComponent from 'webapp/components/reports/editor/fragment/board';
import ReportsEditorFragmentImageComponent from 'webapp/components/reports/editor/fragment/image';
import ReportsEditorFragmentPlaceholderComponent from 'webapp/components/reports/editor/fragment/placeholder';
import ReportsEditorFragmentStoryTextComponent from 'webapp/components/reports/editor/fragment/story-text';
import ReportsEditorFragmentTitleComponent from 'webapp/components/reports/editor/fragment/title';
import ReportsEditorHeaderComponent from 'webapp/components/reports/editor/header';
import ReportsFragmentGridComponent from 'webapp/components/reports/fragment-grid/index';
import ReportsPagelessBottomInfoComponent from 'webapp/components/reports/pageless-bottom-info';

import Board from 'webapp/models-custom/board';
import type ReportSlide from 'webapp/models-custom/report-slide';
import type ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';
import { DEFAULT_SLIDE_TITLE } from 'webapp/models/report-slide';

import type { PagelessTourSettings } from 'webapp/services/flow/tour/reports-pageless';
import type { CreateFragmentResponseToGrid } from 'webapp/services/model-manager/report-slide-fragments';

type PagelessAction = 'addEmoji' | 'addCover';
type Option = PopupListItem<PagelessAction>;

export default class ReportsEditorPagelessComponent extends ReportsEditorBaseComponent<Args> {
	@service('logger') declare loggerService: Service.Logger;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('account') declare accountService: Service.Account;
	@service('tour') declare tourService: Service.Tour;
	@service('flow/tour/reports-pageless')
	declare pagelessTourService: Service.Flow.Tour.ReportsPageless;
	@service('event-manager') declare eventManagerService: Service.EventManager;

	node: HTMLElement | null = null;

	availableTourId = TourId.REPORTS__PAGELESS_CUSTOMIZE;

	customizeTourSetting: PagelessTourSettings = {
		id: TourId.REPORTS__PAGELESS_CUSTOMIZE,
		checkStartConditions: this.checkCustomizeTourStartConditions.bind(this),
	};

	@tracked contentWidth = 0;
	@tracked wrapperWidth = 0;

	@tracked creatingCoverSlide = false;

	orderProperty = OrderProperty.GLOBALORDER;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// slideCount does not work if switching to slides and back to pageless
		if (this.args.model.isNew || this.args.model.slides.length === 0) {
			next(this, this.createCoverSlide);
			// show intro tour for new blank reports if eligible
			next(this, this.checkAndShowReportsTourIntro);
		}

		// setup tour bindings
		schedule('afterRender', () => {
			this.setupTourBindings();
		});

		$(window).on('resize.datawallcenter', this.debounceUpdateSizes);
	}

	get fontSize(): number {
		return Board.getFontsizeFromWidth(this.contentWidth);
	}

	get shownFragments() {
		// cover image fragment is not shown in pageless view
		return this.args.model.shownFragments;
	}

	get bgTheme() {
		return this.navigationService.theme.id;
	}

	get hideBranding(): boolean {
		return this.accountService.removeBrandingOnFeatures;
	}

	setupTourBindings() {
		this.pagelessTourService.bindTourCheck(this.availableTourId, this, () => {
			if (this.customizeTourSetting.checkStartConditions()) {
				this.pagelessTourService.startTour(this.availableTourId);
			}
		});
	}

	protected override updateFragmentSortOrder(
		fragment: ReportSlideFragment,
		ordersData: OrdersData,
	) {
		// first, check if the fragment has moved to a different slide
		// if so, we need to set that slide so globalOrder calculations work correctly

		// if fragment is new, we don't have an id
		const globalOrderForMovedFragment = fragment.id
			? ordersData.find((item) => item.id === fragment.id)?.order
			: undefined;
		let fragmentMovedToAnotherSlide = false;

		if (globalOrderForMovedFragment) {
			const globalPositionForMovedFragment = getPositionFromOrder(
				globalOrderForMovedFragment,
			);

			const slideMovedTo = this.args.model.getSlideWithGlobalRowIndex(
				globalPositionForMovedFragment.row,
			);

			fragmentMovedToAnotherSlide = !!slideMovedTo && slideMovedTo.id !== fragment.slide?.id;
			if (fragmentMovedToAnotherSlide) {
				fragment.reportSlideId = slideMovedTo!.numericId!;
			}
		}

		// apply order
		// must do this before sending fragment change save, to not send old order
		const promise = super.updateFragmentSortOrder(fragment, ordersData);

		if (fragmentMovedToAnotherSlide) {
			void this.addToSaveQueue(fragment);
		}

		return promise;
	}

	@action
	override onFragmentChange(
		fragment: ReportSlideFragment,
		action: ReportFragmentAction,
		data?: ReportFragmentActionData,
	) {
		return super.onFragmentChange(fragment, action, data).then(() => {
			const actionsWithoutRearrange = [
				ReportFragmentAction.RESIZE,
				ReportFragmentAction.CONTENT,
				ReportFragmentAction.CHANGE_TYPE, // only reason to recheck would be someone swiching type from/to title which is an edge case
			];
			if (!actionsWithoutRearrange.includes(action)) {
				// don't wait for the promise
				void this.rearrangeSlides();
			}

			// trigger tour check after fragment is added
			if (action === ReportFragmentAction.ADD) {
				schedule('afterRender', () => {
					this.pagelessTourService.checkTour();
				});
			}
		});
	}

	@action
	async rearrangeSlides() {
		this.logToConsole('---- REARRANGING SLIDES START');

		// - "logical" slide split (boards seperate, from title, max 2 rows)
		await this.seperateSlidesOptimally();

		// - if slide has title fragment, set slide title from the fragment
		await this.updateSlideTitles();

		// if no fragments on slide, delete slide
		await this.deleteEmptySlides();

		this.logToConsole('---- REARRANGING SLIDES END');
	}

	@action
	setupView(node: HTMLElement): void {
		this.node = node;
		this.updateSizes();
	}

	@action
	teardownView(): void {
		$(window).off('resize.datawallcenter', this.debounceUpdateSizes);

		// cleanup tour bindings
		this.pagelessTourService.unbindTourCheck(this.availableTourId, this);
	}

	@action
	debounceUpdateSizes() {
		debounce(this, this.updateSizes, 100);
	}

	@action
	updateSizes(): void {
		const content = this.node?.querySelector<HTMLElement>('.pageless-content');

		if (!content) {
			return;
		}

		const wrapper = content.closest<HTMLElement>('.pageless-wrapper');

		if (!wrapper) {
			return;
		}

		this.contentWidth = content.offsetWidth;
		this.wrapperWidth = wrapper.offsetWidth;
		// to match font size on slides
		if (this.node) {
			this.node.style.fontSize = `${this.fontSize}px`;
		}
	}

	private createCoverSlide(): void {
		this.creatingCoverSlide = true;
		void this.mmReportSlidesService
			.createNewFromLayout(this.args.model, NewReportSlideLayout.COVER)
			.then(() => {
				this.creatingCoverSlide = false;
			});
	}

	@action
	private checkAndShowReportsTourIntro(): void {
		if (this.tourService.isEligibleForReportsPagelessAddBlocksTour) {
			const existingPagelessReports = this.mmReportsService.normalReportModels.filter(
				(report) => report.isPageless && report.id !== this.args.model.id,
			);

			if (existingPagelessReports.length === 0) {
				this.mmReportsService.showReportsTourIntroPopup();
			}
		}
	}

	/**
	 * optimally separates slides to ensure all slides are valid.
	 * - processes slides from back to front (same as rows on each slide - so full slides are always infront)
	 * - for each invalid slide (>2 rows, board with other fragments, title not first):
	 *   - removes rows from bottom until remaining fragments form a valid group
	 *   - moves the valid group to a new slide created before the current slide
	 *   - leaves invalid fragments on current slide to be re-processed in next iteration
	 * - after separation, attempts to merge single-row slides with next slide if valid
	 * - special handling for cover slides (allows up to 3 rows)
	 *
	 * additionaly we added row validation to ensure that the setup is valid
	 * we have an issue which causes invalid setups with multiple databoards on the same slide in the same row
	 * in the case of invalid row we move the overflowing fragments to a new row at the end of the slide
	 */
	private async seperateSlidesOptimally() {
		this.logToConsole('---- NEW LOCAL OPTIMIZATION START');

		const slides = this.args.model.sortedSlides;
		const fragmentPromises: Promise<unknown>[] = [];
		let slidesWereReordered = false;

		const getRowsFromFragments = (fragments: ReportSlideFragment[]): Set<number> => {
			return new Set(fragments.map((f) => f.position.row));
		};

		const getSortedFragments = (slide: ReportSlide): ReportSlideFragment[] => {
			return sortBy(slide.fragments, 'order');
		};

		const getRowCount = (slide: ReportSlide): number => {
			if (slide.fragments.length === 0) return 0;
			return getRowsFromFragments(slide.fragments).size;
		};

		// check if fragments CAN form a valid slide (standard 2-row max, no cover handling)
		const canFragmentsFormValidSlide = (fragmentsToCheck: ReportSlideFragment[]): boolean => {
			if (fragmentsToCheck.length === 0) return true;

			// max 2 rows
			const rows = getRowsFromFragments(fragmentsToCheck);
			if (rows.size > 2) {
				return false;
			}

			// if contains board, must ONLY be the board
			const boardFragments = fragmentsToCheck.filter((f) => f.isTypeBoard);
			if (boardFragments.length > 0 && fragmentsToCheck.length > 1) {
				return false;
			}

			// if contains title, title must be first
			const titleFragments = fragmentsToCheck.filter((f) => f.isTypeTitle);
			if (titleFragments.length > 0) {
				const hasNonTitleBeforeTitle = titleFragments.some((titleFrag) => {
					return fragmentsToCheck.some(
						(f) => f !== titleFrag && f.order < titleFrag.order,
					);
				});
				if (hasNonTitleBeforeTitle) {
					return false;
				}
			}

			return true;
		};

		const isRowValid = (fragmentsInRow: ReportSlideFragment[]): boolean => {
			if (fragmentsInRow.length === 0) return true;

			// max 3 fragments per row
			if (fragmentsInRow.length > 3) return false;

			// if contains board, must be alone in the row
			const hasBoard = fragmentsInRow.some((f) => f.isTypeBoard);
			if (hasBoard && fragmentsInRow.length > 1) {
				return false;
			}

			// if contains title, must be alone in the row
			const hasTitle = fragmentsInRow.some((f) => f.isTypeTitle);
			if (hasTitle && fragmentsInRow.length > 1) {
				return false;
			}

			return true;
		};

		// fix invalid rows by moving last fragment to a new row at the end of the slide
		const fixInvalidRows = (slide: ReportSlide): void => {
			let fragments = getSortedFragments(slide);
			if (fragments.length === 0) return;

			// keep fixing until all rows are valid
			let foundInvalidRow: boolean;
			do {
				fragments = getSortedFragments(slide);
				const rows = Array.from(getRowsFromFragments(fragments)).sort((a, b) => a - b);
				foundInvalidRow = false;

				for (const rowNum of rows) {
					const fragmentsInRow = fragments.filter((f) => f.position.row === rowNum);

					if (!isRowValid(fragmentsInRow)) {
						foundInvalidRow = true;

						// take last fragment in row (highest column)
						const sortedByColumn = [...fragmentsInRow].sort(
							(a, b) => b.position.column - a.position.column,
						);
						const fragmentToMove = sortedByColumn[0];

						if (fragmentToMove) {
							// move to a new row at the end of the slide
							const maxRow = Math.max(...fragments.map((f) => f.position.row));
							const newRow = maxRow + 1;
							const newOrder = getOrderFromPosition(newRow, 1);

							this.logToConsole(
								`Fixing invalid row ${rowNum}: moving fragment from column ${fragmentToMove.position.column} to new row ${newRow}`,
							);

							fragmentToMove.order = newOrder;
							fragmentPromises.push(this.addToSaveQueue(fragmentToMove));
						}
						break;
					}
				}
			} while (foundInvalidRow);
		};

		const createSlideBefore = async (beforeSlide: ReportSlide): Promise<ReportSlide> => {
			const beforeOrder = beforeSlide.order;
			const allSlides = Array.from(this.args.model.sortedSlides);

			// new slide takes the current slide's order
			const newSlide = this.mmReportSlidesService.createNewModel({
				reportId: this.args.model.numericId!,
				title: DEFAULT_SLIDE_TITLE,
				order: beforeOrder,
			});

			// WAIT for the new slide to be saved before using it
			await this.addToSaveQueue(newSlide);

			this.logToConsole(`Creating slide before ${beforeSlide.numericId}`);

			// build array of slide IDs in correct order (including new slide at beforeOrder position)
			// slides before insertion point + new slide + slides at or after insertion point
			const reorderedIds = allSlides
				.filter((slide) => slide.order < beforeOrder)
				.concat(newSlide)
				.concat(allSlides.filter((slide) => slide.order >= beforeOrder))
				.map((slide) => slide.numericId!);

			this.mmReportSlidesService.reorderReportLocally(this.args.model, reorderedIds);
			slidesWereReordered = true;

			return newSlide;
		};

		const moveFragmentsToSlide = (
			fragmentsToMove: ReportSlideFragment[],
			targetSlide: ReportSlide,
		): void => {
			if (fragmentsToMove.length === 0) return;

			const existingFragments = getSortedFragments(targetSlide);

			const uniqueRows = Array.from(getRowsFromFragments(fragmentsToMove)).sort(
				(a, b) => a - b,
			);
			const rowsBeingAdded = uniqueRows.length;

			// find the first row index on target slide (could be 1, 2, 3... if fragments were deleted)
			const targetStartRow =
				existingFragments.length > 0
					? Math.min(...existingFragments.map((f) => f.position.row))
					: 1;

			existingFragments.forEach((existingFragment) => {
				const currentRow = existingFragment.position.row;
				const newRow = currentRow + rowsBeingAdded;
				const newOrder = getOrderFromPosition(newRow, existingFragment.position.column);

				this.logToConsole(
					`Shifting existing fragment from row ${currentRow} to row ${newRow}`,
				);

				existingFragment.order = newOrder;
				fragmentPromises.push(this.addToSaveQueue(existingFragment));
			});

			// add moved fragments at the target start row, compacting them
			const rowMapping = new Map(
				uniqueRows.map((oldRow, index) => [oldRow, targetStartRow + index]),
			);

			fragmentsToMove.forEach((fragment) => {
				const oldRow = fragment.position.row;
				const newRow = rowMapping.get(oldRow)!;
				const newOrder = getOrderFromPosition(newRow, fragment.position.column);

				this.logToConsole(
					`Moving fragment from slide ${fragment.reportSlideId} row ${oldRow} to slide ${targetSlide.numericId} row ${newRow}`,
				);

				fragment.order = newOrder;
				fragment.reportSlideId = targetSlide.numericId!;
				fragmentPromises.push(this.addToSaveQueue(fragment));
			});
		};

		// SPECIAL HANDLING: cover slide
		// handle this separately before main loop
		const firstSlide = slides[0];
		let firstSlideWasHandledAsCover = false;

		if (firstSlide?.isTypeCoverPageless) {
			const firstSlideFragments = getSortedFragments(firstSlide);
			const allRows = Array.from(getRowsFromFragments(firstSlideFragments)).sort(
				(a, b) => a - b,
			);

			// collect fragments that need to leave the cover slide:
			// 1. fragments in rows 4+ (cover allows max 3 rows)
			// 2. board fragments if there are other fragments (boards must be alone)
			const allowedRows = allRows.slice(0, 3);
			const fragmentsToMove = firstSlideFragments.filter(
				(f) =>
					!allowedRows.includes(f.position.row) ||
					(f.isTypeBoard && firstSlideFragments.length > 1),
			);

			if (fragmentsToMove.length > 0) {
				this.logToConsole(
					`Cover slide: moving ${fragmentsToMove.length} fragments to new slide`,
				);

				// create new slide after cover and move all fragments to it
				// main loop will handle further separation if needed (e.g., board + text)
				const newSlide = this.mmReportSlidesService.createNewModel({
					reportId: this.args.model.numericId!,
					title: DEFAULT_SLIDE_TITLE,
					order: firstSlide.order + 1,
				});
				await this.addToSaveQueue(newSlide);
				moveFragmentsToSlide(fragmentsToMove, newSlide);

				// normalize slide orders to avoid conflicts with existing slides
				const existingSlides = this.args.model.sortedSlides.filter((s) => s !== newSlide);
				const reorderedIds = [firstSlide, newSlide]
					.concat(existingSlides.filter((s) => s !== firstSlide))
					.map((slide) => slide.numericId!);

				this.mmReportSlidesService.reorderReportLocally(this.args.model, reorderedIds);
				slidesWereReordered = true;
			}

			// Mark that we've handled the first slide as a cover
			firstSlideWasHandledAsCover = true;
		}

		// process each slide and find valid groups to move
		// skip first slide ONLY if it was handled as a cover
		for (let i = slides.length - 1; i >= 0; i--) {
			const slide = slides[i]!;

			if (firstSlideWasHandledAsCover && slide.id === firstSlide?.id) {
				continue;
			}

			// process each slide until it is valid
			let slideNeedsProcessing: boolean;
			do {
				const fragments = getSortedFragments(slide);
				slideNeedsProcessing = false;

				if (fragments.length === 0) {
					continue;
				}

				// check if current slide is valid -> skip
				if (canFragmentsFormValidSlide(fragments)) {
					continue;
				}

				// slide is invalid - fix any invalid rows first
				fixInvalidRows(slide);

				// re-fetch fragments after fixing invalid rows
				const fixedFragments = getSortedFragments(slide);

				// slide is invalid - remove rows from BOTTOM until remaining fragments are valid
				const allRows = Array.from(getRowsFromFragments(fixedFragments)).sort(
					(a, b) => b - a,
				);

				const fragmentsToMove: ReportSlideFragment[] = [];
				let fragmentsToKeep = [...fixedFragments];
				let foundValidGroup = false;

				// try removing rows from bottom, one by one until remaining fragments form a valid slide
				for (const rowToRemove of allRows) {
					const fragmentsInRow = fixedFragments.filter(
						(f) => f.position.row === rowToRemove,
					);
					fragmentsToMove.push(...fragmentsInRow);
					fragmentsToKeep = fragmentsToKeep.filter((f) => !fragmentsToMove.includes(f));

					this.logToConsole(
						`Removed row ${rowToRemove}. Checking if remaining ${fragmentsToKeep.length} fragments form valid slide`,
					);

					if (canFragmentsFormValidSlide(fragmentsToKeep)) {
						foundValidGroup = true;
						this.logToConsole(
							`Found valid group! Moving ${fragmentsToKeep.length} fragments to new slide`,
						);
						break;
					}
				}

				if (!foundValidGroup) {
					this.logToConsole(
						`ERROR: Could not find valid group on slide ${slide.numericId!} - this should not happen!`,
					);
					continue;
				}

				// move the VALID fragments to NEW slide BEFORE current
				// leave the INVALID fragments on current slide to be re-processed
				const newSlide = await createSlideBefore(slide);
				moveFragmentsToSlide(fragmentsToKeep, newSlide);

				this.logToConsole(
					`Moved ${fragmentsToKeep.length} fragments to new slide. Current slide now has ${fragmentsToMove.length} fragments to re-check.`,
				);

				slideNeedsProcessing = true;
			} while (slideNeedsProcessing);

			// if current slide only has 1 row, try to merge it with next slide
			const finalFragments = getSortedFragments(slide);
			const finalRowCount = getRowCount(slide);

			if (finalRowCount === 1 && finalFragments.length > 0) {
				const nextSlide = slide.nextSlide!;

				if (nextSlide) {
					const nextSlideFragments = getSortedFragments(nextSlide);
					const nextSlideRowCount = getRowCount(nextSlide);

					// check if next slide can accept this row (has 1 or fewer rows)
					if (nextSlideRowCount <= 1) {
						// check if combined fragments would form a valid slide
						const combinedFragments = [...finalFragments, ...nextSlideFragments];
						if (canFragmentsFormValidSlide(combinedFragments)) {
							moveFragmentsToSlide(finalFragments, nextSlide);
						}
					}
				}
			}
		}

		this.logToConsole('---- NEW LOCAL OPTIMIZATION END');

		if (slidesWereReordered) {
			const finalOrder = this.args.model.sortedSlides.map((s) => s.numericId!);
			this.mmReportSlidesService.commitReorderForReport(this.args.model, finalOrder);
		}

		return Promise.allSettled(fragmentPromises);
	}

	private updateSlideTitles() {
		const slidePromises: Promise<unknown>[] = [];
		this.args.model.sortedSlides.forEach((slide) => {
			const headerFragment = slide.headerFragment;
			// only if the title is the default title
			if (headerFragment && slide.title === DEFAULT_SLIDE_TITLE) {
				const title = stripAllHtml(headerFragment.value.content || '');
				if (title !== slide.title) {
					this.logToConsole('Updating slide title from title fragment', {
						old: slide.title,
						new: title,
					});
					slide.title = title;
					slidePromises.push(this.addToSaveQueue(slide));
				}
			}
		});
		return Promise.allSettled(slidePromises);
	}

	private deleteEmptySlides() {
		const emptySlides = this.args.model.slides.filter((slide) => {
			return !slide.fragments.length;
		});

		if (emptySlides.length) {
			this.logToConsole(
				'Empty slides found',
				emptySlides.map((item) => item.numericId),
			);
		}

		// when deleting slide, check if it has description content.
		// if yes, and previous / next does not, move them to previous / next (in that order)
		emptySlides.forEach((slide) => {
			if (slide.descriptionContent) {
				let slideToMoveTo: ReportSlide | undefined;
				// if previous or next has no description, pick those
				// else just append
				const previousSlide = slide.previousSlide,
					nextSlide = slide.nextSlide;
				if (previousSlide && !previousSlide.description) {
					slideToMoveTo = previousSlide;
				} else if (nextSlide && !nextSlide.description) {
					slideToMoveTo = nextSlide;
				} else {
					slideToMoveTo = previousSlide || nextSlide || undefined;
				}
				if (slideToMoveTo) {
					this.logToConsole(
						`Moving slide description from ${slide.id} to ${slideToMoveTo.id}`,
						slideToMoveTo.description ? ', appending' : '',
					);
					// cannot call setDescription bcos we'll change user data and timing
					if (slideToMoveTo.description) {
						// append
						slideToMoveTo.description.content += slide.descriptionContent;
						slideToMoveTo.description = copyObject(slideToMoveTo.description);
					} else {
						slideToMoveTo.description = slide.description;
					}

					void this.addToSaveQueue(slideToMoveTo);
				}
			}
		});

		return Promise.allSettled(emptySlides.map((slide) => slide.deleteAndCatch()));
	}

	private logToConsole(message: string, ...args: unknown[]) {
		this.loggerService.logToConsole(`[P->S] ${message}`, ...args);
	}

	private checkCustomizeTourStartConditions(): boolean {
		return this.pagelessActions.length > 0;
	}

	// -------  Cover and emoji actions -------

	get pagelessActions(): Option[] {
		const actions: Option[] = [];

		const coverHeaderFragment = this.args.model.sortedSlides[0]?.headerFragment,
			isPagelessCoverHeader = coverHeaderFragment?.slide?.isTypeCoverPageless,
			hasEmoji = isPagelessCoverHeader && hasLeadingEmoji(this.args.model.title);

		if (!hasEmoji) {
			actions.push({
				text: 'Add emoji',
				action: 'addEmoji',
				icon: 'MoodSmile',
			});
		}

		if (!this.args.model.cover) {
			actions.push({
				text: 'Add cover',
				action: 'addCover',
				icon: 'Image',
			});
		}

		return actions;
	}

	@action
	doPagelessAction(actionName: PagelessAction): void {
		this.pagelessTourService.endTour(TourId.REPORTS__PAGELESS_CUSTOMIZE);
		this.pagelessTourService.checkTour();

		switch (actionName) {
			case 'addEmoji':
				void this.handleAddEmoji();
				break;
			case 'addCover':
				this.handleCoverAction();
				break;
		}
	}

	@action
	async handleAddEmoji(): Promise<void> {
		// open emoji dialog after new title fragment is rendered
		const scheduleEmojiDialogOpen = () => {
			schedule('afterRender', () => {
				const fragment = document.querySelector('.fragment--title');
				const editorElement = fragment?.querySelector<HTMLElement>('.html-editor-target');
				if (editorElement?.id) {
					this.openEmojiDialog(editorElement);
				}
			});
		};

		const titleFragment = this.shownFragments.find((f) => f.isTypeTitle);
		if (!titleFragment) {
			if (!this.args.model.slideCount) {
				await this.createSlide();
			}

			// if there is no title fragment, create one in the first row
			const addData: ReportFragmentActionAddData = {
				fragmentType: NewReportSlideFragmentLayout.TITLE,
				rowIndex: 1,
				columnIndex: null,
			};
			const firstSlide = this.args.model.firstSlide!;

			await this.mmReportSlideFragmentsService
				.addFragmentFromLayout(addData.fragmentType, firstSlide)
				.then(async ({ fragment }: CreateFragmentResponseToGrid) => {
					fragment.isNewlyCreated = true;
					await this.onFragmentChange(fragment, ReportFragmentAction.ADD, addData);
				})
				.catch(() => {
					// do nothing
				});
		}

		scheduleEmojiDialogOpen();
	}

	@tracked coverSubmitInProgress = false;
	@action
	handleCoverAction(action = CoverAction.ADD): void {
		if (this.coverSubmitInProgress) {
			return;
		}

		this.openCoverImageSelect(action);
	}

	private openCoverImageSelect(action: CoverAction): void {
		const report = this.args.model;
		const imageAspect = 4;

		// Only use existing cover data for EDIT action
		let originalUrl = '';
		let crop = null;

		if (action === CoverAction.EDIT && report.cover) {
			originalUrl = report.coverOriginal || report.cover;
			crop = report.coverCrop;
		}

		const onFinish: PopupData.Common.ImageUpload['onFinish'] = (url, originalUrl, crop) => {
			report.cover = url;
			report.coverOriginal = originalUrl;
			report.coverCrop = crop;
			void this.saveCoverChanges();
		};

		this.mmReportsService.openCoverPopup(
			onFinish,
			'',
			originalUrl || undefined,
			action,
			crop,
			imageAspect,
		);
	}

	private saveCoverChanges(): Promise<void> {
		const report = this.args.model;
		this.coverSubmitInProgress = true;

		return report
			.save()
			.then(() => {
				this.mmReportsService.triggerReportEdited();
			})
			.catch((error: RequestError) => {
				this.requestService.showAllValidationErrors(error);
			})
			.finally(() => {
				this.coverSubmitInProgress = false;
			});
	}

	private async createSlide(): Promise<void> {
		if (!this.args.model.slideCount) {
			const slide = this.mmReportSlidesService.createNewModel({
				reportId: this.args.model.numericId!,
			});
			await this.addToSaveQueue(slide);
		}
	}

	private openEmojiDialog(targetElement: HTMLElement) {
		const editor = tinymce.get(targetElement.id);
		const openEmojiDialogWithDelay = () => {
			editor.focus();
			editor.selection.setCursorLocation(editor.getBody(), 0);

			const handleEmojiInsert = () => {
				const content = stripAllHtml(editor.getContent());
				if (hasLeadingEmoji(content)) {
					// insert space after leading emoji
					const leadingEmoji = getLeadingEmoji(content);
					editor.setContent(
						editor.getContent().replace(leadingEmoji, leadingEmoji + ' '),
					);
				}
			};

			editor.on('NodeChange', handleEmojiInsert);

			// without later the emoji dialog does not have necessary data yet
			later(() => {
				editor.execCommand('mceEmoticons');
			}, 100);

			// remove handler after emoji dialog closes
			editor.on('CloseWindow', () => {
				editor.off('NodeChange', handleEmojiInsert);
			});
		};

		if (editor) {
			if (editor.initialized) {
				openEmojiDialogWithDelay();
			} else {
				editor.on('init', openEmojiDialogWithDelay);
			}
		}
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		node.dispatchEvent(new Event(containerEventName));
	}
	<template>
		<ReportsEditorHeaderComponent
			@model={{@model}}
			@changeViewMode={{@changeViewMode}}
			@closeEditor={{@closeEditor}}
		/>

		<div
			id="report-editor_body"
			class="report_ct report_ct--pageless"
			{{visibleCheckContainer true}}
			{{didUpdate this.triggerContentChange this.shownFragments}}
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			<div
				class="pageless-wrapper bgTheme-{{this.bgTheme}}
					colorTheme-1
					{{if this.isBlockSettingsOpen 'block-settings-open'}}"
			>
				{{#if @model.isTemplate}}
					<div class="template-label"></div>
				{{/if}}

				{{#if @model.cover}}
					<ReportsCoverPagelessComponent
						@cover={{@model.cover}}
						@handleCoverAction={{this.handleCoverAction}}
					/>
				{{/if}}

				<div class="pageless-content">
					<div class="pageless-actions-wrapper">
						<div
							class={{joinClasses
								(array
									"pageless-actions-container"
									(if
										this.creatingCoverSlide
										"pageless-actions-container--disabled"
									)
								)
							}}
						>
							{{#each this.pagelessActions as |item|}}
								<a
									role="button"
									class="button pageless-action-btn"
									{{on "click" (fn this.doPagelessAction (definite item.action))}}
								>
									{{#if item.icon}}
										<CommonUiIconComponent @name={{item.icon}} @size="sm" />
									{{/if}}
									{{item.text}}
								</a>
							{{/each}}
						</div>
					</div>

					<ReportsFragmentGridComponent
						@model={{this.shownFragments}}
						@report={{@model}}
						@isEditable={{this.isFragmentGridEditable}}
						@orderProperty={{this.orderProperty}}
						@onFragmentChange={{this.onFragmentChange}}
						as |fragments Item onFragmentChange|
					>
						{{#each fragments as |fragment|}}
							<Item @model={{fragment}}>
								{{#if fragment.isTypeBoard}}
									<ReportsEditorFragmentBoardComponent
										@model={{fragment}}
										@width={{this.wrapperWidth}}
									/>
								{{else if fragment.isTypeImage}}
									<ReportsEditorFragmentImageComponent
										@model={{fragment}}
										@onFragmentChange={{onFragmentChange}}
									/>
								{{else if fragment.isTypeBlock}}
									<ReportsEditorFragmentBlockComponent
										@model={{fragment}}
										@onFragmentChange={{onFragmentChange}}
										@changeBlockFragmentToEdit={{this.changeBlockFragmentToEdit}}
										@isBeingEdited={{eq
											fragment.id
											this.blockFragmentToEdit.id
										}}
									/>
								{{else if fragment.isTypeStoryText}}
									<ReportsEditorFragmentStoryTextComponent
										@model={{fragment}}
										@onFragmentChange={{onFragmentChange}}
										@isBeingEdited={{eq
											fragment.id
											this.blockFragmentToEdit.id
										}}
									/>
								{{else if fragment.isTypeTitle}}
									<ReportsEditorFragmentTitleComponent
										@model={{fragment}}
										@onFragmentChange={{onFragmentChange}}
									/>
								{{else if fragment.isTypePlaceholder}}
									<ReportsEditorFragmentPlaceholderComponent
										@model={{fragment}}
										@onFragmentChange={{onFragmentChange}}
									/>
								{{/if}}
							</Item>
						{{/each}}
					</ReportsFragmentGridComponent>
				</div>

				<ReportsPagelessBottomInfoComponent
					@model={{@model}}
					@hideBranding={{this.hideBranding}}
					@canEdit={{true}}
				/>
			</div>

			<ReportsEditorBlockSettingsComponent
				@fragment={{this.blockFragmentToEdit}}
				@onClose={{fn this.changeBlockFragmentToEdit null}}
			/>
		</div>
	</template>
}
